import { router } from "@inertiajs/react";
import { useState } from "react";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import { Field, Input, Select } from "@/components/ui/Field";
import type { SatelliteEvent } from "@/interfaces/satellite_event";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface Prefill {
  name: string | null;
  email: string | null;
  birth_month: number | null;
  birth_year: number | null;
}

// The whole signup. No account, no login, no Hackatime - an attendee is
// somebody turning up to a boba shop for an afternoon, and every field here has
// to earn its place against the people it costs us.
export default function RsvpForm({
  event,
  events,
  ageRange,
  heardAboutOptions,
  prefill,
}: {
  // The event page passes the one event it is showing. The standalone /rsvp
  // page passes the whole list instead and lets the attendee pick.
  event?: SatelliteEvent;
  events?: SatelliteEvent[];
  ageRange: { min: number; max: number };
  heardAboutOptions: string[];
  prefill: Prefill;
}) {
  const choices = events ?? [];
  const [eventId, setEventId] = useState(
    event
      ? String(event.id)
      : choices.length === 1
        ? String(choices[0].id)
        : "",
  );
  const [name, setName] = useState(prefill.name ?? "");
  const [email, setEmail] = useState(prefill.email ?? "");
  const [birthMonth, setBirthMonth] = useState(
    prefill.birth_month ? String(prefill.birth_month) : "",
  );
  const [birthYear, setBirthYear] = useState(
    prefill.birth_year ? String(prefill.birth_year) : "",
  );
  const [heardAbout, setHeardAbout] = useState("");
  const [heardAboutOther, setHeardAboutOther] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Only the years that could possibly be inside the age window, newest first -
  // a teenager shouldn't scroll past eighty years to find theirs.
  const thisYear = new Date().getFullYear();
  const years = Array.from(
    { length: ageRange.max - ageRange.min + 2 },
    (_, i) => thisYear - ageRange.min - i,
  );
  const otherPicked = heardAbout === "Somewhere else";

  return (
    <Panel title="rsvp.form">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitting(true);
          router.post(
            `/events/${eventId}/rsvp`,
            {
              name,
              email,
              birth_month: birthMonth,
              birth_year: birthYear,
              heard_about: otherPicked ? heardAboutOther : heardAbout,
            },
            { onFinish: () => setSubmitting(false) },
          );
        }}
      >
        <p className="text-body text-ink-muted">
          Free, and it takes about ten seconds. We'll email you a link to
          confirm your spot.
        </p>

        {!event && (
          <Field label="Which one are you going to?">
            <Select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              required
            >
              <option value="">Pick a city</option>
              {choices.map((choice) => (
                <option
                  key={choice.id}
                  value={choice.id}
                  disabled={choice.full}
                >
                  {choice.location_label}
                  {choice.starts_at_short_label
                    ? ` - ${choice.starts_at_short_label}`
                    : ""}
                  {choice.full ? " (full)" : ""}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Your name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
            />
          </Field>

          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
        </div>

        <Field
          label="When were you born?"
          hint={`Boba Bash is for ${ageRange.min}-${ageRange.max} year olds, so we ask - month and year is enough.`}
        >
          <div className="flex gap-3">
            <Select
              value={birthMonth}
              onChange={(e) => setBirthMonth(e.target.value)}
              aria-label="Birth month"
              required
            >
              <option value="">Month</option>
              {MONTHS.map((month, i) => (
                <option key={month} value={i + 1}>
                  {month}
                </option>
              ))}
            </Select>
            <Select
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              aria-label="Birth year"
              required
            >
              <option value="">Year</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </div>
        </Field>

        <Field label="Where did you find this event?">
          <Select
            value={heardAbout}
            onChange={(e) => setHeardAbout(e.target.value)}
            required
          >
            <option value="">Pick one</option>
            {heardAboutOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>

        {otherPicked && (
          <Field label="Where was that?">
            <Input
              value={heardAboutOther}
              onChange={(e) => setHeardAboutOther(e.target.value)}
              maxLength={120}
              required
            />
          </Field>
        )}

        <Button
          type="submit"
          disabled={submitting || !eventId}
          className="self-start"
        >
          {submitting ? "Sending…" : "RSVP"}
        </Button>
      </form>
    </Panel>
  );
}
